package org.moxultimate.topwithmox.productcrud.api

import io.ktor.client.*
import io.ktor.client.request.*
import kotlinx.browser.window
import kotlinx.coroutines.await
import kotlinx.coroutines.flow.Flow
import kotlinx.coroutines.flow.flow
import kotlinx.serialization.json.Json
import org.khronos.webgl.Int8Array
import org.moxultimate.topwithmox.core.base.BaseAPI.baseAPI
import org.moxultimate.topwithmox.core.handler.APIHandler
import org.moxultimate.topwithmox.core.state.APIState
import org.moxultimate.topwithmox.product.data.ProductContentResponse
import org.moxultimate.topwithmox.product.data.ProductContentTagResponse
import org.moxultimate.topwithmox.product.data.ProductContentTypeResponse
import org.moxultimate.topwithmox.product.data.ProductResponse
import org.w3c.files.Blob
import org.w3c.xhr.FormData

/**
 * ProductCRUDRepository
 * Handles all write operations for product content and product items:
 * create, update, delete, and saving tags/types.
 * Read operations live in ProductRepository to keep concerns separate.
 *
 * @param httpClient Ktor HTTP client used for POST, PUT, and DELETE requests.
 */
class ProductCRUDRepository(
    private val httpClient: HttpClient,
) {
    private val baseUrl = "$baseAPI/product-contents"
    private val tagUrl = "$baseAPI/product-content-tag"
    private val typeUrl = "$baseAPI/product-content-type"
    private val productsUrl = "$baseAPI/products"

    /**
     * createProductContent
     * Creates a new product content entry via POST /product-contents using multipart/form-data.
     * Uses window.fetch + FormData so an optional thumbnail image can be included.
     *
     * @param title Required section title.
     * @param description Optional short paragraph shown below the title.
     * @param videoLink Optional YouTube or external link.
     * @param tags Optional pipe-separated tag string.
     * @param type Optional content type label.
     * @param imageBytes Optional raw thumbnail image bytes.
     * @return Flow emitting Loading → Success(ProductContentResponse) or Error.
     */
    fun createProductContent(
        title: String,
        description: String? = null,
        videoLink: String? = null,
        tags: String? = null,
        type: String? = null,
        imageBytes: ByteArray? = null,
    ): Flow<APIState<ProductContentResponse>> = flow {
        emit(APIState.Loading)
        emit(performContentCreate(title, description, videoLink, tags, type, imageBytes))
    }

    private suspend fun performContentCreate(
        title: String,
        description: String?,
        videoLink: String?,
        tags: String?,
        type: String?,
        imageBytes: ByteArray?,
    ): APIState<ProductContentResponse> {
        return try {
            val form = FormData()
            form.append("title", title)
            description?.takeIf { it.isNotBlank() }?.let { form.append("description", it) }
            videoLink?.takeIf { it.isNotBlank() }?.let { form.append("video_link", it) }
            tags?.takeIf { it.isNotBlank() }?.let { form.append("tags", it) }
            type?.takeIf { it.isNotBlank() }?.let { form.append("type", it) }
            if (imageBytes != null) {
                val imageBlob = Blob(arrayOf(imageBytes.unsafeCast<Int8Array>().buffer), imageType)
                form.append("image", imageBlob, "thumbnail.webp")
            }

            val init: dynamic = js("{}")
            init.method = "POST"
            init.body = form
            val response = window.fetch(baseUrl, init).await()
            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }
            APIState.Success(lenientJson.decodeFromString<ProductContentResponse>(response.text().await()))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Create failed")
        }
    }

    /**
     * updateProductContent
     * Updates an existing product content entry via PUT /product-contents/{id} using multipart/form-data.
     * Uses window.fetch + FormData so an optional new thumbnail image can be included.
     *
     * @param id The content ID to update.
     * @param imageBytes Optional raw bytes for a new thumbnail; omit to keep the existing one.
     * @return Flow emitting Loading → Success(ProductContentResponse) or Error.
     */
    fun updateProductContent(
        id: Long,
        title: String? = null,
        description: String? = null,
        videoLink: String? = null,
        tags: String? = null,
        type: String? = null,
        imageBytes: ByteArray? = null,
    ): Flow<APIState<ProductContentResponse>> = flow {
        emit(APIState.Loading)
        emit(performContentUpdate(id, title, description, videoLink, tags, type, imageBytes))
    }

    private suspend fun performContentUpdate(
        id: Long,
        title: String?,
        description: String?,
        videoLink: String?,
        tags: String?,
        type: String?,
        imageBytes: ByteArray?,
    ): APIState<ProductContentResponse> {
        return try {
            val form = FormData()
            title?.takeIf { it.isNotBlank() }?.let { form.append("title", it) }
            description?.takeIf { it.isNotBlank() }?.let { form.append("description", it) }
            videoLink?.takeIf { it.isNotBlank() }?.let { form.append("video_link", it) }
            tags?.takeIf { it.isNotBlank() }?.let { form.append("tags", it) }
            type?.takeIf { it.isNotBlank() }?.let { form.append("type", it) }
            if (imageBytes != null) {
                val imageBlob = Blob(arrayOf(imageBytes.unsafeCast<Int8Array>().buffer), imageType)
                form.append("image", imageBlob, "thumbnail.webp")
            }

            val init: dynamic = js("{}")
            init.method = "PUT"
            init.body = form
            val response = window.fetch("$baseUrl/$id", init).await()
            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }
            APIState.Success(lenientJson.decodeFromString<ProductContentResponse>(response.text().await()))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Update failed")
        }
    }

    /**
     * deleteProductContent
     * Deletes a product content entry via DELETE /product-contents/{id}.
     *
     * @param id The content ID to delete.
     * @return Flow emitting Loading → Success or Error.
     */
    fun deleteProductContent(id: Long): Flow<APIState<String>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall { httpClient.delete("$baseUrl/$id") })
    }

    /**
     * createProduct
     * Creates a new product entry under a content via POST /products.
     * Uses browser FormData + fetch() when [imageBytes] is provided — Ktor's multipart builder
     * generates Content-Disposition headers that Spring 6 rejects.
     *
     * @param contentId Parent content ID this product belongs to.
     * @param imageBytes Raw image bytes; triggers the FormData path when non-null.
     * @return Flow emitting Loading → Success(ProductResponse) or Error.
     */
    fun createProduct(
        contentId: Long,
        brand: String,
        name: String,
        label: String? = null,
        description: String,
        pros: String? = null,
        cons: String? = null,
        shopeeLink: String? = null,
        shopeePrice: String? = null,
        shopeePromo: String? = null,
        lazadaLink: String? = null,
        lazadaPrice: String? = null,
        lazadaPromo: String? = null,
        amazonLink: String? = null,
        amazonPrice: String? = null,
        amazonPromo: String? = null,
        tiktokLink: String? = null,
        tiktokPrice: String? = null,
        tiktokPromo: String? = null,
        imageBytes: ByteArray? = null,
    ): Flow<APIState<ProductResponse>> = flow {
        emit(APIState.Loading)
        if (imageBytes != null) {
            emit(performCreateWithImage(
                contentId, brand, name, label, description, pros, cons,
                shopeeLink, shopeePrice, shopeePromo,
                lazadaLink, lazadaPrice, lazadaPromo,
                amazonLink, amazonPrice, amazonPromo,
                tiktokLink, tiktokPrice, tiktokPromo,
                imageBytes,
            ))
        } else {
            emit(APIHandler.safeApiCall {
                httpClient.post(productsUrl) {
                    parameter("content_id", contentId)
                    parameter("brand", brand)
                    parameter("name", name)
                    parameter("description", description)
                    label?.takeIf { it.isNotBlank() }?.let { parameter("label", it) }
                    pros?.takeIf { it.isNotBlank() }?.let { parameter("pros", it) }
                    cons?.takeIf { it.isNotBlank() }?.let { parameter("cons", it) }
                    shopeeLink?.takeIf { it.isNotBlank() }?.let { parameter("shopee_link", it) }
                    shopeePrice?.takeIf { it.isNotBlank() }?.let { parameter("shopee_price", it) }
                    shopeePromo?.takeIf { it.isNotBlank() }?.let { parameter("shopee_promo", it) }
                    lazadaLink?.takeIf { it.isNotBlank() }?.let { parameter("lazada_link", it) }
                    lazadaPrice?.takeIf { it.isNotBlank() }?.let { parameter("lazada_price", it) }
                    lazadaPromo?.takeIf { it.isNotBlank() }?.let { parameter("lazada_promo", it) }
                    amazonLink?.takeIf { it.isNotBlank() }?.let { parameter("amazon_link", it) }
                    amazonPrice?.takeIf { it.isNotBlank() }?.let { parameter("amazon_price", it) }
                    amazonPromo?.takeIf { it.isNotBlank() }?.let { parameter("amazon_promo", it) }
                    tiktokLink?.takeIf { it.isNotBlank() }?.let { parameter("tiktok_link", it) }
                    tiktokPrice?.takeIf { it.isNotBlank() }?.let { parameter("tiktok_price", it) }
                    tiktokPromo?.takeIf { it.isNotBlank() }?.let { parameter("tiktok_promo", it) }
                }
            })
        }
    }

    private suspend fun performCreateWithImage(
        contentId: Long,
        brand: String,
        name: String,
        label: String?,
        description: String,
        pros: String?,
        cons: String?,
        shopeeLink: String?,
        shopeePrice: String?,
        shopeePromo: String?,
        lazadaLink: String?,
        lazadaPrice: String?,
        lazadaPromo: String?,
        amazonLink: String?,
        amazonPrice: String?,
        amazonPromo: String?,
        tiktokLink: String?,
        tiktokPrice: String?,
        tiktokPromo: String?,
        imageBytes: ByteArray,
    ): APIState<ProductResponse> {
        return try {
            val form = FormData()
            form.append("content_id", contentId.toString())
            form.append("brand", brand)
            form.append("name", name)
            form.append("description", description)
            label?.takeIf { it.isNotBlank() }?.let { form.append("label", it) }
            pros?.takeIf { it.isNotBlank() }?.let { form.append("pros", it) }
            cons?.takeIf { it.isNotBlank() }?.let { form.append("cons", it) }
            shopeeLink?.takeIf { it.isNotBlank() }?.let { form.append("shopee_link", it) }
            shopeePrice?.takeIf { it.isNotBlank() }?.let { form.append("shopee_price", it) }
            shopeePromo?.takeIf { it.isNotBlank() }?.let { form.append("shopee_promo", it) }
            lazadaLink?.takeIf { it.isNotBlank() }?.let { form.append("lazada_link", it) }
            lazadaPrice?.takeIf { it.isNotBlank() }?.let { form.append("lazada_price", it) }
            lazadaPromo?.takeIf { it.isNotBlank() }?.let { form.append("lazada_promo", it) }
            amazonLink?.takeIf { it.isNotBlank() }?.let { form.append("amazon_link", it) }
            amazonPrice?.takeIf { it.isNotBlank() }?.let { form.append("amazon_price", it) }
            amazonPromo?.takeIf { it.isNotBlank() }?.let { form.append("amazon_promo", it) }
            tiktokLink?.takeIf { it.isNotBlank() }?.let { form.append("tiktok_link", it) }
            tiktokPrice?.takeIf { it.isNotBlank() }?.let { form.append("tiktok_price", it) }
            tiktokPromo?.takeIf { it.isNotBlank() }?.let { form.append("tiktok_promo", it) }
            val imageBlob = Blob(arrayOf(imageBytes.unsafeCast<Int8Array>().buffer), imageType)
            form.append("image", imageBlob, "image.webp")

            val init: dynamic = js("{}")
            init.method = "POST"
            init.body = form
            val response = window.fetch(productsUrl, init).await()
            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }
            APIState.Success(lenientJson.decodeFromString<ProductResponse>(response.text().await()))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Create failed")
        }
    }

    /**
     * updateProduct
     * Updates an existing product entry via PUT /products/{id}.
     * Only non-null, non-blank fields are included in the request.
     *
     * @param id Product ID to update.
     * @param imageBytes Raw image bytes; triggers the FormData path when non-null.
     * @return Flow emitting Loading → Success(ProductResponse) or Error.
     */
    fun updateProduct(
        id: Long,
        brand: String? = null,
        name: String? = null,
        label: String? = null,
        description: String? = null,
        pros: String? = null,
        cons: String? = null,
        shopeeLink: String? = null,
        shopeePrice: String? = null,
        shopeePromo: String? = null,
        lazadaLink: String? = null,
        lazadaPrice: String? = null,
        lazadaPromo: String? = null,
        amazonLink: String? = null,
        amazonPrice: String? = null,
        amazonPromo: String? = null,
        tiktokLink: String? = null,
        tiktokPrice: String? = null,
        tiktokPromo: String? = null,
        imageBytes: ByteArray? = null,
    ): Flow<APIState<ProductResponse>> = flow {
        emit(APIState.Loading)
        emit(performUpdate(
            id, brand, name, label, description, pros, cons,
            shopeeLink, shopeePrice, shopeePromo,
            lazadaLink, lazadaPrice, lazadaPromo,
            amazonLink, amazonPrice, amazonPromo,
            tiktokLink, tiktokPrice, tiktokPromo,
            imageBytes,
        ))
    }

    /**
     * performUpdate
     * Always uses window.fetch + FormData because the backend PUT endpoint declares
     * consumes = ["multipart/form-data"]. Ktor query-param requests are rejected.
     * The image field is only appended when [imageBytes] is non-null.
     */
    private suspend fun performUpdate(
        id: Long,
        brand: String?,
        name: String?,
        label: String?,
        description: String?,
        pros: String?,
        cons: String?,
        shopeeLink: String?,
        shopeePrice: String?,
        shopeePromo: String?,
        lazadaLink: String?,
        lazadaPrice: String?,
        lazadaPromo: String?,
        amazonLink: String?,
        amazonPrice: String?,
        amazonPromo: String?,
        tiktokLink: String?,
        tiktokPrice: String?,
        tiktokPromo: String?,
        imageBytes: ByteArray?,
    ): APIState<ProductResponse> {
        return try {
            val form = FormData()
            brand?.takeIf { it.isNotBlank() }?.let { form.append("brand", it) }
            name?.takeIf { it.isNotBlank() }?.let { form.append("name", it) }
            label?.takeIf { it.isNotBlank() }?.let { form.append("label", it) }
            description?.takeIf { it.isNotBlank() }?.let { form.append("description", it) }
            pros?.takeIf { it.isNotBlank() }?.let { form.append("pros", it) }
            cons?.takeIf { it.isNotBlank() }?.let { form.append("cons", it) }
            shopeeLink?.takeIf { it.isNotBlank() }?.let { form.append("shopee_link", it) }
            shopeePrice?.takeIf { it.isNotBlank() }?.let { form.append("shopee_price", it) }
            shopeePromo?.takeIf { it.isNotBlank() }?.let { form.append("shopee_promo", it) }
            lazadaLink?.takeIf { it.isNotBlank() }?.let { form.append("lazada_link", it) }
            lazadaPrice?.takeIf { it.isNotBlank() }?.let { form.append("lazada_price", it) }
            lazadaPromo?.takeIf { it.isNotBlank() }?.let { form.append("lazada_promo", it) }
            amazonLink?.takeIf { it.isNotBlank() }?.let { form.append("amazon_link", it) }
            amazonPrice?.takeIf { it.isNotBlank() }?.let { form.append("amazon_price", it) }
            amazonPromo?.takeIf { it.isNotBlank() }?.let { form.append("amazon_promo", it) }
            tiktokLink?.takeIf { it.isNotBlank() }?.let { form.append("tiktok_link", it) }
            tiktokPrice?.takeIf { it.isNotBlank() }?.let { form.append("tiktok_price", it) }
            tiktokPromo?.takeIf { it.isNotBlank() }?.let { form.append("tiktok_promo", it) }
            if (imageBytes != null) {
                val imageBlob = Blob(arrayOf(imageBytes.unsafeCast<Int8Array>().buffer), imageType)
                form.append("image", imageBlob, "image.webp")
            }

            val init: dynamic = js("{}")
            init.method = "PUT"
            init.body = form
            val response = window.fetch("$productsUrl/$id", init).await()
            if (!response.ok) {
                val code = response.status.toInt()
                val text = response.text().await()
                return APIState.Error(APIHandler.handleErrorMessage(code, text), code)
            }
            APIState.Success(lenientJson.decodeFromString<ProductResponse>(response.text().await()))
        } catch (e: Throwable) {
            APIState.Error(e.message ?: "Update failed")
        }
    }

    /**
     * deleteProductItem
     * Deletes a product entry via DELETE /products/{id}.
     *
     * @param id Product ID to delete.
     * @return Flow emitting Loading → Success or Error.
     */
    fun deleteProductItem(id: Long): Flow<APIState<String>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall { httpClient.delete("$productsUrl/$id") })
    }

    /**
     * saveTag
     * Registers a new tag via POST /product-content-tag?tag=String.
     *
     * @param tag Tag label to persist.
     * @return Flow emitting Loading → Success(ProductContentTagResponse) or Error.
     */
    fun saveTag(tag: String): Flow<APIState<ProductContentTagResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.post(tagUrl) { parameter("tag", tag) }
        })
    }

    /**
     * saveType
     * Registers a new content type via POST /product-content-type?type=String.
     *
     * @param type Type label to persist.
     * @return Flow emitting Loading → Success(ProductContentTypeResponse) or Error.
     */
    fun saveType(type: String): Flow<APIState<ProductContentTypeResponse>> = flow {
        emit(APIState.Loading)
        emit(APIHandler.safeApiCall {
            httpClient.post(typeUrl) { parameter("type", type) }
        })
    }
}

/** Lenient JSON parser — tolerates extra fields returned by the server. */
private val lenientJson = Json { ignoreUnknownKeys = true }

/** MIME type options object passed to the Blob constructor for product images. */
private val imageType: dynamic = js("{type: 'image/webp'}")
